import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectActiveConfigVersion } from 'src/app/masterdata/state/master-data.selectors';
import { selectUserLanguage } from 'src/app/core/state/core.selectors';
import { TranslationUtils } from 'src/app/shared/utils/translation-utils';
import { Status } from '../../models/criterions/Status';
import { selectAnalysis, selectAnalysisPlantCode, selectAnalysisState, selectIsNotGroupAnalysis, selectPlantMuSchG } from '../../state/analysis.selectors';
import { DataMM13ElementItem } from '../models/DataMM13ElementItem';
import { Cr13State, featureKey } from './cr13.state';
import { Cr13Utils } from '../utils/cr13-utils';
import { OpInstructionsHazardEntry } from '../../models/OpInstructionsHazardEntry';

const selectCr13State = createFeatureSelector<Cr13State>(featureKey);

export const selectTreeNode = createSelector(selectCr13State, state => state.selectedTreeNode);

export const selectDefMM13ElementFromConfigVersion = (id: number) =>
    createSelector(selectActiveConfigDefMM13, mainElements => mainElements?.elements?.find(item => item.id === id));

export const selectMainGroupFromDefMM13Element = (id: number) =>
    createSelector(selectDefMM13ElementFromConfigVersion(id), mainElement => mainElement?.mainGroup);

export const selectDataFromDefMM13Element = (id: number) =>
    createSelector(selectDefMM13ElementFromConfigVersion(id), mainElement => ({
        mainGroup: mainElement?.mainGroup ?? null,
        subGroup: mainElement?.subGroup ?? null,
        hazardElement: mainElement?.hazardElement ?? null,
        templateType: mainElement?.templateType ?? null
    }));

export const selectMainAndSubGroupFromDefMM13Element = (id: number) =>
    createSelector(selectDataFromDefMM13Element(id), mainElement => ({
        mainGroup: mainElement?.mainGroup ?? null,
        subGroup: mainElement?.subGroup ?? null
    }));

export const selectHazardType = (id: number) => createSelector(selectDefMM13ElementFromConfigVersion(id), element => element?.templateType);

export const selectTitleFromMainElement = (id: number) =>
    createSelector(selectDefMM13ElementFromConfigVersion(id), selectUserLanguage, (mainElement, userLanguage) => {
        const hazardElemSubSubGroup = mainElement?.hazardElement ?? '';
        const hazardElemTitle = mainElement?.mainGroup + '.' + mainElement?.subGroup + '.' + hazardElemSubSubGroup;
        return mainElement ? hazardElemTitle + ' ' + TranslationUtils.translateTextValue(mainElement?.name, userLanguage) : mainElement;
    });

export const selectActiveConfigDefMM13 = createSelector(selectActiveConfigVersion, state => state?.defMM13);

export const selectHasExtraTreeCategories = createSelector(selectAnalysisPlantCode, plant => {
    if (!plant) {
        return false;
    }
    return plant.includes('09.') || plant.includes('10.') || plant.includes('20.02') || plant.includes('20.00');
});

export const selectMenuTree = createSelector(selectActiveConfigDefMM13, selectHasExtraTreeCategories, (state, hasExtraCategories) => {
    if (!hasExtraCategories) {
        return state?.treeMenu.filter(element => element.mainGroup !== 11 && element.mainGroup !== 12 && element.mainGroup !== 13);
    }
    return state?.treeMenu;
});

export const selectVibrationCalculatorFile = createSelector(selectActiveConfigDefMM13, state => state?.vibrationCalculatorUrl);

export const selectDataMM13 = createSelector(selectAnalysis, state => state?.dataMM13);

export const selectHasSupportAnalysis = createSelector(selectAnalysis, state => state?.supportAnalysis && state.supportAnalysis?.length > 0);

export const selectHasGroupAnalysis = createSelector(selectAnalysis, state => state?.groupAnalysis && state.groupAnalysis.groupAnalysisElements?.length > 0);

export const selectExistingHazards = createSelector(selectDataMM13, state => {
    let items = (state?.items as DataMM13ElementItem[]) ?? [];
    let itemsSupport = (state?.itemsSupport as DataMM13ElementItem[]) ?? [];
    return [...items, ...itemsSupport];
});

export const selectHasLinkedAnalysis = createSelector(selectExistingHazards, items => {
    return items.filter(item => item.parent !== null).length > 0;
});

export const selectHazardFromId = (id: number) => createSelector(selectDataMM13, state => state?.items?.find(item => item.id === id));

export const selectHazardsToShow = createSelector(selectExistingHazards, selectTreeNode, selectActiveConfigDefMM13, (dataMM13, selectedTreeNode) => {
    return dataMM13?.filter(item => (selectedTreeNode?.nodeData.length ? selectedTreeNode?.nodeData.includes(item.defMM13ElementId) : true));
});

export const selectMeasureCatalog = createSelector(selectCr13State, selectUserLanguage, (state, language) => {
    return state?.measureCatalog.map(measure => {
        return {
            key: measure,
            name: TranslationUtils.translateTextValue(measure.name, language)
        };
    });
});

export const selectAnalysisMM13 = createSelector(selectAnalysis, state => state?.dataMM13);

export const selectAnalysisMM13RiValue = createSelector(selectAnalysisMM13, state => state?.rzValue);

export const selectAnalysisMM13ShiValue = createSelector(selectAnalysisMM13, state => state?.shiValue);

export const selectAnalysisMM13ShiRiColor = createSelector(selectAnalysisMM13, state => state?.color);

export const selectCr13AllHazardsNoStress = createSelector(selectAnalysisMM13, state => state?.noStress ?? false);

export const selectDataMM13MainElements = createSelector(selectAnalysisMM13, state => (state ? state.mainElements : []));

export const selectDataMM13MainElement = (id: number) =>
    createSelector(selectDataMM13MainElements, mainElements => mainElements.find(item => item.defMM13ElementId === id));

export const selectActiveItem = createSelector(selectAnalysisState, state => state?.analysis?.dataMM13.activeItem ?? null);

export const selectActiveItemProblem = createSelector(selectActiveItem, state => state?.problem);

export const selectMeasureFromId = (id: number) => createSelector(selectActiveItemProblem, state => state?.measures?.find(item => item.id === id));

export const selectActiveItemCountermeasures = createSelector(selectActiveItemProblem, state => state?.measures ?? []);

export const selectAttachmentsFromHazard = createSelector(selectActiveItem, state => state?.attachments);

export const selectActiveItemRealizedCountermeasures = createSelector(
    selectActiveItemCountermeasures,
    measures => measures?.filter(measure => measure.status === Status.REALIZED) ?? []
);

export const selectActiveMeasure = createSelector(selectActiveItemProblem, state => state?.activeMeasure);

export const selectRiskMatrixItems = createSelector(selectActiveConfigDefMM13, state => state?.matrixItems ?? []);

export const selectIsNewHazardCreation = createSelector(selectCr13State, state => state?.isNewHazardCreation ?? false);

export const selectIsNewMeasureCreation = createSelector(selectCr13State, state => state?.isNewMeasureCreation ?? false);

export const selectShowCountermeasureFormPopup = createSelector(selectCr13State, state => state?.showCountermeasureFormPopup);

export const selectValidatingMeasure = createSelector(selectCr13State, state => state?.validatingMeasure);

export const selectGettingUser = createSelector(selectCr13State, state => state?.gettingUser);

export const selectOpInstructions = createSelector(selectDataMM13, state => state?.mm13OpInstruction ?? []);

export const selectHazardsByDefMM13ElementIds = (defMM13ElementIds: number[]) =>
    createSelector(selectExistingHazards, hazards => hazards?.filter(item => defMM13ElementIds.includes(item.defMM13ElementId)));

export const selectChoiceData = createSelector(selectCr13State, state => state?.choiceData);

export const selectSearchingChoiceData = createSelector(selectCr13State, state => state?.searchingChoiceData);

export const selectAnalysisHasRiskClassification = createSelector(
    selectPlantMuSchG,
    selectAnalysisPlantCode,
    (plantMuSchG, analysisPlant) => plantMuSchG || analysisPlant === '09.20' || analysisPlant === '09.30'
);

export const selectShouldShowClassificationCheckboxes = (id: number) =>
    createSelector(
        selectAnalysisHasRiskClassification,
        selectMainGroupFromDefMM13Element(id),
        (analysisHasRiskClassification, mainGroup) => analysisHasRiskClassification && mainGroup === 4
    );

export const selectCr13Attachments = createSelector(selectDataMM13, state => state?.attachments);

export const selectIsActionOnTableLoading = createSelector(selectCr13State, state => state?.loadingActionOnTable);

export const selectCr08HazardIsCreated = createSelector(selectActiveConfigVersion, selectExistingHazards, (activeConfigVersion, hazards) => {
    const cr08HazardDefMm13ElementId = activeConfigVersion?.defMM13?.elements.find(element => element.mainGroup === 8 && element.subGroup === 2)?.id;
    return hazards.some(hazard => hazard.defMM13ElementId === cr08HazardDefMm13ElementId && hazard.connectedItem);
});

export const selectCr10HazardIsCreated = createSelector(selectActiveConfigVersion, selectExistingHazards, (activeConfigVersion, hazards) => {
    const cr10HazardDefMm13ElementId = activeConfigVersion?.defMM13?.elements.find(element => element.mainGroup === 1 && element.subGroup === 8)?.id;
    return hazards.some(hazard => hazard.defMM13ElementId === cr10HazardDefMm13ElementId && hazard.connectedItem);
});

export const selectIsTreeExpanded = createSelector(selectCr13State, state => state?.isTreeExpanded);

export const selectOperatingInstructionsHazards = createSelector(
    selectExistingHazards,
    selectActiveConfigDefMM13,
    selectUserLanguage,
    (hazards, activeConfigDefMM13, language) => {
        const opInstructionsLanguage = language === 'DE' ? 'DE' : 'EN';
        return hazards
            .filter(hazard => (hazard.problem?.measures ?? []).some(measure => measure.status === Status.REALIZED))
            .map(
                hazard =>
                    ({
                        title: hazard.problem?.description || null,
                        id: hazard.problem?.id || null,
                        description: hazard.problem?.descriptionLong || null,
                        category: Cr13Utils.getHazardDescriptionName(
                            activeConfigDefMM13?.elements?.find(element => element.id === hazard.defMM13ElementId),
                            opInstructionsLanguage
                        ),
                        rzValue: hazard.rzValue,
                        color: hazard.color,
                        measures: (hazard.problem?.measures ?? []).filter(measure => measure.status === Status.REALIZED)
                    } as OpInstructionsHazardEntry)
            );
    }
);

export const selectShowCountermeasuresResponsiblePopup = createSelector(selectCr13State, state => state?.showCountermeasuresResponsiblePopup);

export const selectDisablePasteHazardButton = createSelector(
    selectTreeNode,
    selectIsNotGroupAnalysis,
    (selectedTreeNode, isNotGroup) => (!selectedTreeNode?.isLastChild && !selectedTreeNode?.isAllHazards) || !isNotGroup
);

export const selectDefMM13Element = (mainGroup: number, subGroup: number, hazardElement: number) =>
    createSelector(selectActiveConfigDefMM13, defMM13 =>
        defMM13?.elements.find(item => item.mainGroup == mainGroup && item.subGroup == subGroup && item.hazardElement == hazardElement)
    );

export const selectDefMM13DataAndRiskClassification = (id: number) =>
    createSelector(selectDataFromDefMM13Element(id), selectAnalysisHasRiskClassification, (defMM13Data, hasRiskClassification) => ({
        defMM13Data,
        hasRiskClassification
    }));

export const selectTreeNodeAndExtraCategories = createSelector(selectTreeNode, selectHasExtraTreeCategories, (treeNode, hasExtraCategories) => ({
    treeNode,
    hasExtraCategories
}));
